import {t, useLanguage} from '../i18n';
import LanguagePicker from '../i18n/LanguagePicker';
// src/components/AvatarDropdown.js
import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "../styles/loopLayers.css";

const BRAND = "#00675f";

function MenuIcon({ type }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.15,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (type === "business") {
    return (
      <svg {...common}>
        <path d="M3 21h18" />
        <path d="M5 21V7l7-4 7 4v14" />
        <path d="M9 21v-6h6v6" />
      </svg>
    );
  }

  if (type === "bookings") {
    return (
      <svg {...common}>
        <path d="M6.5 4.5A2.5 2.5 0 0 1 9 2h6a2.5 2.5 0 0 1 2.5 2.5V21L12 17.8 6.5 21V4.5Z" />
      </svg>
    );
  }



  if (type === "loyalty") {
    return (
      <svg {...common}>
        <path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" />
        <path d="M2 7h20v5H2z" />
        <path d="M12 22V7" />
        <path d="M12 7H7.5A2.5 2.5 0 1 1 10 4.5C10 6.2 12 7 12 7Z" />
        <path d="M12 7h4.5A2.5 2.5 0 1 0 14 4.5C14 6.2 12 7 12 7Z" />
      </svg>
    );
  }

  if (type === "rating") {
    return (
      <svg {...common}>
        <path d="M12 17.3 6.8 20l1-5.8-4.2-4.1 5.8-.8L12 4l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 17.3Z" />
      </svg>
    );
  }

  if (type === "pending") {
    return (
      <svg {...common}>
        <path d="M12 8v5l3 2" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    );
  }

  if (type === "plus") {
    return (
      <svg {...common}>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    );
  }

  if (type === "logout") {
    return (
      <svg {...common}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="M16 17l5-5-5-5" />
        <path d="M21 12H9" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M20 21a8 8 0 1 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function getInitials(name, email) {
  const cleanName = String(name || "").trim();

  if (cleanName) {
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
  }

  const cleanEmail = String(email || "").trim();
  if (cleanEmail) return cleanEmail.slice(0, 2).toUpperCase();

  return "U";
}

function MenuRow({ icon, label, badge, danger = false, onClick }) {
  const handleClick = (event) => {
    try {
      event.preventDefault();
      event.stopPropagation();
    } catch (_) {}
    if (typeof onClick === "function") onClick(event);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={(event) => {
        try { event.stopPropagation(); } catch (_) {}
      }}
      onTouchStart={(event) => {
        try { event.stopPropagation(); } catch (_) {}
      }}
      className={`avatar-menu-row ${danger ? "is-danger" : ""}`}
      role="menuitem"
    >
      <span className="avatar-menu-icon">
        <MenuIcon type={icon} />
      </span>

      <span className="avatar-menu-label">{label}</span>

      {badge ? (
        <span className="avatar-menu-badge">{badge}</span>
      ) : !danger ? (
        <span className="avatar-menu-chevron">›</span>
      ) : null}
    </button>
  );
}

export default function AvatarDropdown(props) {
 useLanguage();
  const {
    userName = "",
    userEmail = "",
    avatarUrl = null,
    open: openProp = undefined,
    setOpen: setOpenProp = undefined,
    pendingCount = 0,
    onShowUserProfile = () => {},
    onAdminManage = () => {},
    onShowMyBookings = () => {},
    onShowMyLoyalty = () => {},
    onShowPending = () => {},
    onShowRatings = null,
    onShowRateServices = null,
    onCreateBusiness = () => {},
    onLogout = () => {},
    showBusinessAdmin = false,
    onPlatformAdmin = null,
    hideTrigger = false,
    externalTriggerRef = null,
  } = props;

  const controlled = typeof setOpenProp === "function";
  const [openState, setOpenState] = useState(false);
  const open = controlled ? Boolean(openProp) : openState;
  const setOpen = controlled ? setOpenProp : setOpenState;

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const actionLockRef = useRef(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (delay = 200) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, delay);
  };

  const handleOpen = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [setOpen]);

  const handleMouseEnter = () => handleOpen();
  const handleMouseLeave = () => scheduleClose(180);

  const handleFocus = () => handleOpen();
  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (!containerRef.current) return;
    if (externalTriggerRef && externalTriggerRef.current && externalTriggerRef.current === next) return;
    if (!next || !containerRef.current.contains(next)) scheduleClose(50);
  };

  useEffect(() => {
    const onDocPointer = (e) => {
      if (!containerRef.current && !menuRef.current) return;

      const target = e.target;

      if (menuRef.current && menuRef.current.contains(target)) return;
      if (containerRef.current && containerRef.current.contains(target)) return;
      if (externalTriggerRef && externalTriggerRef.current && externalTriggerRef.current.contains(target)) return;

      setOpen(false);
    };

    document.addEventListener("pointerdown", onDocPointer, { passive: true });
    document.addEventListener("touchstart", onDocPointer, { passive: true });
    document.addEventListener("mousedown", onDocPointer, { passive: true });

    return () => {
      try { document.removeEventListener("pointerdown", onDocPointer); } catch (_) {}
      try { document.removeEventListener("touchstart", onDocPointer); } catch (_) {}
      try { document.removeEventListener("mousedown", onDocPointer); } catch (_) {}
    };
  }, [setOpen, externalTriggerRef]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  useEffect(() => () => clearCloseTimer(), []);

  const avatarLabel = userName && userName.trim() ? userName : userEmail || "Usuario";
  const initials = String(userName || userEmail || "U").trim().slice(0, 1).toUpperCase();
  const AVATAR_SIZE_PX = 48;

  const [menuStyle, setMenuStyle] = useState(null);
  const triggerEl = externalTriggerRef && externalTriggerRef.current ? externalTriggerRef.current : null;
  const OVERLAY_Z_INDEX = "var(--loop-z-header-panel, 1200)";

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }

    try {
      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      const isMobile = vw <= 720;
      const suggestedWidth = isMobile ? Math.min(vw - 20, 360) : 292;

      // LOOP GLOBAL LAYER SYSTEM R7:
      // The menu is portaled to document.body and uses the shared header-panel layer.
      // It stays above the fixed header but below application dialogs.
      if (isMobile) {
        setMenuStyle({
          position: "fixed",
          left: "10px",
          right: "10px",
          bottom: "calc(max(env(safe-area-inset-bottom, 0px), 34px) + 12px)",
          width: "auto",
          maxHeight: "calc(100dvh - max(env(safe-area-inset-top, 0px), 0px) - max(env(safe-area-inset-bottom, 0px), 34px) - 28px)",
          zIndex: OVERLAY_Z_INDEX,
        });
        return;
      }

      const anchorEl = triggerEl || containerRef.current;
      if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function") {
        setMenuStyle({
          position: "fixed",
          top: "72px",
          right: "12px",
          width: `${suggestedWidth}px`,
          zIndex: OVERLAY_Z_INDEX,
        });
        return;
      }

      const rect = anchorEl.getBoundingClientRect();
      let left = Math.round(rect.right - suggestedWidth);
      let top = Math.round(rect.bottom + 12);

      if (left + suggestedWidth + 12 > vw) left = Math.max(12, vw - suggestedWidth - 12);
      if (left < 12) left = 12;

      if (top + 360 > vh) {
        top = Math.max(12, rect.top - 360 - 12);
      }

      const ms = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        width: `${suggestedWidth}px`,
        zIndex: OVERLAY_Z_INDEX,
      };

      setMenuStyle(ms);
    } catch (_) {
      setMenuStyle(null);
    }
  }, [open, triggerEl]);

  const invokeAndClose = (fn, options = {}) => {
    if (actionLockRef.current) return;
    actionLockRef.current = true;

    try {
      clearCloseTimer();
    } catch (_) {}

    try {
      setOpen(false);
    } catch (_) {}

    const run = () => {
      try {
        if (typeof fn === "function") fn();
      } catch (e) {
        console.warn("[AvatarDropdown] action failed", e);
      } finally {
        setTimeout(() => {
          actionLockRef.current = false;
        }, 450);
      }
    };

    // iOS fix: destructive/session actions should run immediately.
    // Otherwise the first tap can be consumed by the dropdown closing animation.
    if (options.immediate) {
      run();
      return;
    }

    requestAnimationFrame(run);
  };

  const rows = [
    {
      key: "profile",
      icon: "profile",
      label: t("Mi perfil"),
      onClick: () => invokeAndClose(onShowUserProfile),
    },
    {
      key: "bookings",
      icon: "bookings",
      label: t("Mis reservas"),
      onClick: () => invokeAndClose(onShowMyBookings),
    },
    {
      key: "loyalty",
      icon: "loyalty",
      label: t("Mis beneficios"),
      onClick: () => invokeAndClose(onShowMyLoyalty),
    },
    {
      key: "ratings",
      icon: "rating",
      label: t("Calificar servicios"),
      badge: pendingCount > 0 ? (pendingCount > 99 ? "99+" : pendingCount) : null,
      onClick: () => invokeAndClose(onShowRatings || onShowRateServices || onShowPending),
    },
  ].filter(Boolean);

  const fallbackMenuStyle = {
    position: "fixed",
    top: "72px",
    right: "12px",
    width: 292,
    zIndex: OVERLAY_Z_INDEX,
  };

  const menuPanel = open ? (
    <div
      ref={menuRef}
      role="menu"
      aria-label={t("Menú de usuario")}
      className="avatar-menu-panel"
      style={menuStyle ? menuStyle : fallbackMenuStyle}
    >
      <div className="avatar-menu-grabber" aria-hidden="true" />

      <div className="avatar-menu-head">
        <div className="avatar-menu-head-avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt={avatarLabel} />
          ) : (
            <span>{initials}</span>
          )}
        </div>

        <div className="avatar-menu-head-copy">
          <strong>{userName || t("Mi cuenta")}</strong>
          <span>{userEmail || "Loop Reservas"}</span>
        </div>
      </div>

      {showBusinessAdmin || onPlatformAdmin ? <div className="avatar-workspace">
        <div className="avatar-section-label">{t("Espacio de trabajo")}</div>
        {showBusinessAdmin ? <MenuRow icon="business" label={t("Gestionar mis negocios")} onClick={() => invokeAndClose(onAdminManage)} /> : null}
        {showBusinessAdmin ? <p>{t("Agenda, clientes, servicios y equipo")}</p> : null}
        {onPlatformAdmin ? <MenuRow icon="business" label={t("Administración de Loop")} onClick={() => invokeAndClose(onPlatformAdmin)} /> : null}
      </div> : null}
      <div className="avatar-section-label">{t("Mi cuenta")}</div>
      <div className="avatar-menu-list">
        {rows.map((item) => (
          <MenuRow
            key={item.key}
            icon={item.icon}
            label={item.label}
            badge={item.badge}
            onClick={item.onClick}
          />
        ))}
      </div>

      <div className="avatar-menu-divider" />

      <div style={{padding: "8px 16px"}}>
        <LanguagePicker />
      </div>

      <div className="avatar-menu-list compact">
        <MenuRow
          icon="plus"
          label={t(showBusinessAdmin ? "Agregar otro negocio" : "Crear mi negocio")}
          onClick={() => invokeAndClose(onCreateBusiness)}
        />

        <MenuRow
          icon="logout"
          label={t("Cerrar sesión")}
          danger
          onClick={() => invokeAndClose(onLogout, { immediate: true })}
        />
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={`avatar-menu-container ${hideTrigger ? "avatar-menu-external-trigger" : ""}`}
      onMouseEnter={!hideTrigger ? handleMouseEnter : undefined}
      onMouseLeave={!hideTrigger ? handleMouseLeave : undefined}
      onFocus={!hideTrigger ? handleFocus : undefined}
      onBlur={!hideTrigger ? handleBlur : undefined}
    >
      <style>{styles}</style>

      {!hideTrigger && (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen(!open)}
          className="avatar-menu-trigger"
          title={avatarLabel}
          style={{ width: AVATAR_SIZE_PX, height: AVATAR_SIZE_PX }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={avatarLabel} className="avatar-menu-trigger-img" />
          ) : (
            <span>{initials}</span>
          )}

          {pendingCount > 0 && (
            <span className="avatar-menu-trigger-badge">
              {pendingCount > 99 ? "99+" : pendingCount}
            </span>
          )}
        </button>
      )}

      {menuPanel && (typeof document !== "undefined" && document.body
        ? createPortal(menuPanel, document.body)
        : menuPanel)}
    </div>
  );
}

const styles = `
  .avatar-menu-container {
    position: relative;
    display: inline-block;
  }

  /* An external avatar button owns the layout. Avoid an empty inline line box. */
  .avatar-menu-container.avatar-menu-external-trigger {
    display: block;
    height: 0;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  .avatar-menu-trigger {
    position: relative;
    border: 1px solid rgba(255,255,255,0.35);
    border-radius: 999px;
    background: linear-gradient(135deg, ${BRAND}, #00897f);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    font-size: 15px;
    cursor: pointer;
    box-shadow: 0 14px 30px rgba(0,103,95,0.22);
    overflow: visible;
    transition: transform 160ms ease, box-shadow 160ms ease;
  }

  .avatar-menu-trigger:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 38px rgba(0,103,95,0.28);
  }

  .avatar-menu-trigger:active {
    transform: scale(0.98);
  }

  .avatar-menu-trigger-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 999px;
  }

  .avatar-menu-trigger-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #ef4444;
    color: #ffffff;
    border: 2px solid #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 950;
    line-height: 20px;
    box-shadow: 0 8px 18px rgba(15,23,42,0.18);
  }

  .avatar-menu-panel {
    overflow: hidden;
    border-radius: 24px;
    border: 1px solid rgba(226,232,240,0.96);
    background: rgba(255,255,255,0.98);
    box-shadow: 0 28px 90px rgba(15,23,42,0.22);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    animation: avatarMenuIn 150ms ease-out;
    pointer-events: auto;
  }

  @keyframes avatarMenuIn {
    from {
      opacity: 0;
      transform: translateY(7px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .avatar-menu-grabber {
    display: none;
  }

  .avatar-menu-head {
    padding: 14px;
    background:
      radial-gradient(circle at top left, rgba(0,103,95,0.14), transparent 36%),
      linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .avatar-menu-head-avatar {
    width: 48px;
    height: 48px;
    border-radius: 18px;
    background: linear-gradient(135deg, ${BRAND}, #00897f);
    color: #ffffff;
    box-shadow: 0 12px 26px rgba(0,103,95,0.18);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 950;
    flex: 0 0 auto;
    overflow: hidden;
  }

  .avatar-menu-head-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-menu-head-copy {
    min-width: 0;
    flex: 1;
  }

  .avatar-menu-head-copy strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    line-height: 1.12;
    font-weight: 950;
    letter-spacing: -0.025em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .avatar-menu-head-copy span {
    display: block;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    margin-top: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .avatar-menu-list {
    padding: 7px;
  }

  .avatar-menu-list.compact {
    padding-top: 4px;
  }

  .avatar-menu-row {
    width: 100%;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    min-height: 50px;
    border: 0;
    background: transparent;
    border-radius: 17px;
    padding: 8px 10px;
    display: flex;
    align-items: center;
    gap: 11px;
    text-align: left;
    cursor: pointer;
    transition: background 150ms ease, transform 150ms ease;
  }

  .avatar-menu-row:hover {
    background: #f8fafc;
  }

  .avatar-menu-row:active {
    transform: scale(0.992);
  }

  .avatar-menu-icon {
    width: 36px;
    height: 36px;
    border-radius: 14px;
    background: rgba(0,103,95,0.08);
    color: ${BRAND};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .avatar-menu-label {
    min-width: 0;
    flex: 1;
    display: block;
    color: #0f172a;
    font-size: 14px;
    line-height: 1.1;
    font-weight: 900;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .avatar-menu-chevron {
    color: #cbd5e1;
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
    flex: 0 0 auto;
  }

  .avatar-menu-badge {
    min-width: 25px;
    height: 23px;
    border-radius: 999px;
    padding: 0 8px;
    background: #ef4444;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 950;
    flex: 0 0 auto;
  }

  .avatar-menu-divider {
    height: 1px;
    background: #f1f5f9;
    margin: 0 14px;
  }

  .avatar-menu-row.is-danger .avatar-menu-icon {
    background: #fef2f2;
    color: #dc2626;
  }

  .avatar-menu-row.is-danger .avatar-menu-label {
    color: #dc2626;
  }

  @media (max-width: 720px) {
    .avatar-menu-panel {
      border-radius: 28px;
      animation: avatarSheetIn 170ms ease-out;
    }

    @keyframes avatarSheetIn {
      from {
        opacity: 0;
        transform: translateY(18px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .avatar-menu-grabber {
      display: flex;
      width: 42px;
      height: 5px;
      border-radius: 999px;
      background: #cbd5e1;
      margin: 10px auto 2px;
    }

    .avatar-menu-head {
      padding: 12px 14px 14px;
    }

    .avatar-menu-list {
      padding: 6px;
    }

    .avatar-menu-row {
      min-height: 50px;
      border-radius: 17px;
    }
  }

  /* =========================================================
     LOOP MOBILE OVERLAY SAFE — AVATAR MENU / PHASE 3
     Makes the profile menu act like a proper mobile bottom sheet.
     ========================================================= */
  :root {
    --loop-overlay-safe-bottom: max(env(safe-area-inset-bottom, 0px), 34px);
    --loop-overlay-safe-top: max(env(safe-area-inset-top, 0px), 0px);
  }

  @media (max-width: 720px) {
    .avatar-menu-panel {
      max-height: calc(100dvh - var(--loop-overlay-safe-top) - var(--loop-overlay-safe-bottom) - 28px) !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      border-radius: 28px !important;
      box-shadow: 0 -22px 70px rgba(15,23,42,.24) !important;
    }

    .avatar-menu-head {
      flex: 0 0 auto !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 2 !important;
      background:
        radial-gradient(circle at top left, rgba(0,103,95,0.14), transparent 36%),
        linear-gradient(135deg, #f8fafc 0%, #ffffff 100%) !important;
    }

    .avatar-menu-list {
      flex: 1 1 auto !important;
      min-height: 0 !important;
      overflow-y: auto !important;
      overscroll-behavior: contain !important;
      -webkit-overflow-scrolling: touch !important;
      padding-bottom: 10px !important;
    }

    .avatar-menu-row {
      min-height: 52px !important;
      touch-action: manipulation !important;
    }
  }

`;
